import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { validateFormData, validationRules } from '../../utils/formValidation';
import './style.less';

export const ContactForm = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Pre-fill message from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const service = params.get('service');
    const sqm = params.get('sqm');

    if (service) {
      const currentLang = localStorage.getItem('i18nextLng') || 'bg';
      let message = '';

      if (currentLang === 'bg') {
        message = `Здравейте,\n\nИнтересувам се от услугата: ${service}`;
        if (sqm) {
          message += `\nРазмер на помещението: ${sqm} кв.м`;
        }
        message += '\nМоля за повече информация и оферта.\n\nБлагодаря!';
      } else {
        message = `Hello,\n\nI am interested in the service: ${service}`;
        if (sqm) {
          message += `\nProperty size: ${sqm} sqm`;
        }
        message += '\nPlease provide more information and a quote.\n\nThank you!';
      }

      setFormData((prev) => ({
        ...prev,
        message: message,
      }));
    }
  }, [location.search]);

  const validateForm = () => {
    const newErrors = validateFormData(
      formData,
      {
        name: validationRules.name,
        phone: validationRules.phone,
        email: validationRules.email,
        message: validationRules.message,
      },
      (key) => t(`contact.${key}`)
    );

    if (!privacyConsent) {
      newErrors.privacyConsent = t('contact.errors.privacyConsentRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Subscribe to newsletter if checkbox is checked
        if (subscribeNewsletter && process.env.NODE_ENV !== 'development') {
          try {
            await fetch('/api/newsletter-subscribe.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: formData.email.trim(),
                source: 'contact-form',
                language: i18n.language,
              }),
            });
            localStorage.setItem('newsletter_subscribed', 'true');
          } catch {
            // Newsletter subscription failure shouldn't affect the contact form success
          }
        }
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '' });
        setSubscribeNewsletter(false);
        setPrivacyConsent(false);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="form-group">
        <label htmlFor="name">{t('contact.form.name')}</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={t('contact.form.namePlaceholder')}
          className={errors.name ? 'error' : ''}
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="phone">{t('contact.form.phone')}</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder={t('contact.form.phonePlaceholder')}
          className={errors.phone ? 'error' : ''}
        />
        {errors.phone && <span className="error-message">{errors.phone}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="email">{t('contact.form.email')}</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={t('contact.form.emailPlaceholder')}
          className={errors.email ? 'error' : ''}
        />
        {errors.email && <span className="error-message">{errors.email}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="message">{t('contact.form.message')}</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder={t('contact.form.messagePlaceholder')}
          rows="6"
          className={errors.message ? 'error' : ''}
        />
        {errors.message && <span className="error-message">{errors.message}</span>}
      </div>

      <div className="form-group form-group--checkbox">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={subscribeNewsletter}
            onChange={(e) => setSubscribeNewsletter(e.target.checked)}
            aria-label={t('contact.form.subscribeNewsletter')}
          />
          <span className="checkbox-text">{t('contact.form.subscribeNewsletter')}</span>
        </label>
      </div>

      <div className="form-group form-group--checkbox">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={privacyConsent}
            onChange={(e) => {
              setPrivacyConsent(e.target.checked);
              if (errors.privacyConsent) {
                setErrors((prev) => ({ ...prev, privacyConsent: null }));
              }
            }}
            aria-label={t('contact.form.privacyConsent')}
            aria-invalid={!!errors.privacyConsent}
          />
          <span className="checkbox-text">
            <Trans i18nKey="contact.form.privacyConsent" components={{ 1: <Link to="/privacy-policy" /> }} />
          </span>
        </label>
        {errors.privacyConsent && <span className="error-message">{errors.privacyConsent}</span>}
      </div>

      <button type="submit" className="btn-primary" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <i className="fas fa-spinner fa-spin"></i> {t('contact.form.submitting')}
          </>
        ) : (
          <>
            <i className="fas fa-paper-plane"></i> {t('contact.form.submit')}
          </>
        )}
      </button>

      {submitStatus === 'success' && (
        <div className="status-message success">
          <i className="fas fa-check-circle"></i> {t('contact.success')}
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="status-message error">
          <i className="fas fa-exclamation-circle"></i> {t('contact.error')}
        </div>
      )}
    </form>
  );
};
